import React from 'react';
import styled from 'styled-components';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  max-width: 600px;
  width: 100%;
  padding: 40px;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  
  &:hover {
    color: #333;
  }
`;

const SuccessIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  font-size: 40px;
  color: white;
`;

const Title = styled.h2`
  font-size: 28px;
  font-weight: 800;
  color: #232B3B;
  text-align: center;
  margin-bottom: 16px;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: #666;
  text-align: center;
  margin-bottom: 32px;
  line-height: 1.5;
`;

const UrgencyBox = styled.div`
  background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
  border: 2px solid #ffd700;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 32px;
`;

const UrgencyTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #232B3B;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &::before {
    content: '⚡';
    font-size: 24px;
  }
`;

const UrgencyText = styled.p`
  font-size: 16px;
  color: #333;
  line-height: 1.6;
  margin-bottom: 20px;
`;

const BenefitsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 20px 0;
`;

const BenefitItem = styled.li`
  font-size: 15px;
  color: #333;
  padding: 8px 0;
  padding-left: 28px;
  position: relative;
  
  &::before {
    content: '✓';
    position: absolute;
    left: 0;
    color: #4CAF50;
    font-weight: bold;
    font-size: 18px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 24px;
  
  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const PrimaryButton = styled.button`
  flex: 1;
  background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
  color: #232B3B;
  padding: 16px 24px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(255, 215, 0, 0.4);
  }
`;

const SecondaryButton = styled.button`
  flex: 1;
  background: transparent;
  color: #232B3B;
  padding: 16px 24px;
  border: 2px solid #232B3B;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    background: #232B3B;
    color: white;
  }
`;

const Disclaimer = styled.p`
  font-size: 13px;
  color: #999;
  text-align: center;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #eee;
  font-style: italic;
`;

interface ImmediateConsultationModalProps {
  isOpen: boolean;
  onClose: () => void;
  appointmentDate?: string;
}

export const ImmediateConsultationModal: React.FC<ImmediateConsultationModalProps> = ({ 
  isOpen, 
  onClose,
  appointmentDate 
}) => {
  if (!isOpen) return null;

  const handleCallNow = () => {
    window.location.href = 'tel:800-922-1894';
  };

  const handleNoThanks = () => {
    onClose();
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>×</CloseButton>
        
        <SuccessIcon>✓</SuccessIcon>
        
        <Title>Your Appointment with Cap is Confirmed!</Title>
        <Subtitle>
          {appointmentDate ? 
            `You're scheduled to speak with Cap on ${appointmentDate}.` :
            "We'll confirm your appointment time shortly."
          }
        </Subtitle>

        <UrgencyBox>
          <UrgencyTitle>Want Answers Sooner?</UrgencyTitle>
          <UrgencyText>
            While you're waiting for your personal consultation with Cap, our team of 
            experienced advisors with 20-40 years of expertise is available to help you RIGHT NOW.
          </UrgencyText>
          
          <BenefitsList>
            <BenefitItem>Get immediate answers to your annuity questions</BenefitItem>
            <BenefitItem>Review your current situation</BenefitItem>
            <BenefitItem>Discuss available options and strategies</BenefitItem>
            <BenefitItem>Prepare for your meeting with Cap</BenefitItem>
          </BenefitsList>

          <ButtonGroup>
            <PrimaryButton onClick={handleCallNow}>
              📞 Call Now: 800-922-1894
            </PrimaryButton>
            <SecondaryButton onClick={handleNoThanks}>
              I'll Wait for Cap
            </SecondaryButton>
          </ButtonGroup>
        </UrgencyBox>

        <Disclaimer>
          This immediate consultation is IN ADDITION to your scheduled appointment with Cap. 
          Your appointment with Cap remains confirmed and will not be cancelled or changed.
        </Disclaimer>
      </ModalContent>
    </ModalOverlay>
  );
};